import cn from 'classnames';
import Link from 'next/link';
import React from 'react';
import { useCallback, useState } from 'react';
import Slider from 'react-slick';

import { useRoutesConfig } from '@/hooks/routes-config';
import { CarTileTypes, Vehicle } from '@/types/used-cars/used-cars.types';

import { ReactComponent as ArrowLeft } from '../../../_icons/arrow-left-white.svg';
import { CarTile } from '../../car-tile/car-tile';
import { SliderArrow } from '../../details-car/slider-arrow/slider-arrow';

import styles from './car-slider.module.scss';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

export const CarSlider: React.FC<{
  vehicles?: Vehicle[];
  tileType?: CarTileTypes;
  vehiclesAsComponents?: React.ReactNode[];
  slidesToShow?: number;
  slidesToScroll?: number;
  hasNav?: boolean;
  setSliderDom?: (argument: any) => void;
  infinite?: boolean;
  id?: string;
  variableWidth?: boolean;
  special?: boolean;
}> = ({
  vehicles,
  tileType,
  vehiclesAsComponents,
  slidesToShow = 3,
  slidesToScroll = 1,
  hasNav = true,
  infinite = true,
  variableWidth = false,
  setSliderDom = () => {
    return;
  },
  special,
  id,
}) => {
  const [slider, setSlider] = useState<Slider>();
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(false);
  const vehiclesLength = vehiclesAsComponents?.length || vehicles?.length;
  const routes = useRoutesConfig();

  const setSliderRef = useCallback(
    (slider: Slider) => {
      setSlider(slider);
      setSliderDom(slider);
    },
    [setSlider, setSliderDom],
  );

  const handleNext = () => {
    slider?.slickNext();
  };
  const handlePrev = () => {
    slider?.slickPrev();
  };

  const handleAfterChange = (index: number) => {
    setPrevDisabled(index === 0);
    setNextDisabled(index === (vehiclesLength as number) - slidesToShow);
  };

  return (
    <div
      className={cn(styles.sliderWrapper, {
        [styles.lessSlider as string]: (vehiclesLength as number) < 3,
      })}
      id={id}
      key={id}
    >
      <Slider
        key="car-slider"
        className="car-slider"
        accessibility
        arrows={false}
        slidesToShow={slidesToShow}
        slidesToScroll={slidesToScroll}
        infinite={infinite}
        ref={setSliderRef}
        variableWidth={variableWidth}
        responsive={[
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
              variableWidth: true,
            },
          },
        ]}
        afterChange={handleAfterChange}
      >
        {vehiclesAsComponents
          ? vehiclesAsComponents
          : (vehicles as Vehicle[])?.map((vehicle) => (
              <Link
                href={
                  !vehicle?.special
                    ? `${routes.usedCars.path}/${vehicle.id}`
                    : `${routes.specials.path}/${vehicle.special.special_category.value}/${vehicle.special.id}/${vehicle.id}`
                }
                key={vehicle.id}
                passHref
              >
                <div className={styles.sliderItem} key={vehicle.id}>
                  <CarTile
                    details={vehicle}
                    type={tileType}
                    sliderTile
                    special={special}
                    categoryLink={
                      (vehicle.special &&
                        vehicle.special.special_category.value) ||
                      ''
                    }
                    price={
                      vehicle.special
                        ? vehicle.special.sale_price_incl_vat
                          ? vehicle.special.sale_price_incl_vat
                          : (
                              Number(vehicle.price) -
                              (Number(vehicle.price) *
                                Number(vehicle.special.sale_percentage)) /
                                100
                            ).toFixed(0)
                        : ''
                    }
                    specialId={vehicle.special ? vehicle.special.id : ''}
                    view="tile"
                  />
                </div>
              </Link>
            ))}
      </Slider>
      {hasNav ? (
        <div className={styles.sliderArrows}>
          <SliderArrow
            key={'ArrowLeft'}
            onClick={handlePrev}
            icon={<ArrowLeft />}
            className={cn(styles.sliderArrow, styles.sliderArrowPrev, {
              [styles.sliderArrowDisabled as string]: prevDisabled,
            })}
          />
          <SliderArrow
            key={'ArrowNext'}
            onClick={handleNext}
            icon={<ArrowLeft className={styles.arrowRight} />}
            className={cn(styles.sliderArrow, {
              [styles.sliderArrowDisabled as string]: nextDisabled,
            })}
          />
        </div>
      ) : null}
    </div>
  );
};
